import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { DollarSign } from 'lucide-react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { adjustCreditBank } from '@/services/credit_bank.service'
import { toast } from '@/utils/helpers'

const adjustSchema = z.object({
  amount: z.coerce
    .number({
      required_error: 'Amount is required',
      invalid_type_error: 'Amount must be a number',
    })
    .refine((val) => val !== 0, 'Amount cannot be zero')
    .refine((val) => Math.abs(val) <= 999999, 'Amount cannot exceed $999,999'),
  notes: z
    .string()
    .min(1, 'Notes are required for adjustments')
    .max(500, 'Notes cannot exceed 500 characters'),
})

const AdjustModal = ({ isOpen, onClose, onSuccess, entityId }) => {
  const form = useForm({
    resolver: zodResolver(adjustSchema),
    defaultValues: {
      amount: '',
      notes: '',
    },
  })

  const { mutate: adjust, isPending } = useMutation({
    mutationFn: (data) => adjustCreditBank(entityId, data),
    onError: (err) => {
      toast(err.message, 'error')
    },
    onSuccess: () => {
      onSuccess()
      form.reset()
    },
  })

  const onSubmit = (data) => {
    adjust(data)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adjust Credit Balance</DialogTitle>
          <DialogDescription>
            Manually adjust the credit balance for this account. Use positive values to add
            credits, negative values to deduct credits.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00 (use + or - for add/subtract)"
                        className="pl-9"
                        {...field}
                        disabled={isPending}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Positive values add credits, negative values deduct credits.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Reason for adjustment (required)..."
                      className="resize-none"
                      rows={3}
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    Provide a reason for this manual adjustment (max 500 characters).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Adjusting...' : 'Adjust Balance'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default AdjustModal

AdjustModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
}
